/* ──────────────────────────────────────────────────────────────────────────
 * Apollo Bearing Index — bootstrap
 * assets/interchange-search.js
 *
 * Wires the console together: omnibox (intent chips, grouped autocomplete,
 * recent + saved popover, keyboard shortcuts), tabs with the sliding
 * indicator, mode panels, dimensions panel with the live cross-section,
 * stats count-up, popular chips, sticky compact bar, URL state restore and
 * lazy loading of the decode / equipment / catalogs views.
 *
 * Load order (all `defer`): utils → api → parse → motion → state →
 * views-search → rfq → this file. Lazy views register themselves through
 * IFX.define("view:<name>") when their script loads.
 * ───────────────────────────────────────────────────────────────────────── */
(function () {
  "use strict";
  var IFX = window.IFX;
  if (!IFX || !IFX.api || !IFX.parse || !IFX.motion || !IFX.state) {
    try { console.error("[interchange] core modules missing — check asset uploads"); } catch (e) { /* noop */ }
    return;
  }
  var root = document.querySelector(".ifx");
  if (!root) return;
  var esc = IFX.escapeHtml;

  IFX.api.configure(root.dataset.apiBase || "");

  /* ── refs ────────────────────────────────────────────────────────────── */
  var refs = {
    results: root.querySelector("[data-results]"),
    status: root.querySelector("[data-status]"),
    head: root.querySelector("[data-results-head]"),
    tabs: Array.from(root.querySelectorAll(".ifx-tab")),
    indicator: root.querySelector(".ifx-tabs__indicator"),
    panels: Array.from(root.querySelectorAll(".ifx-panel")),
    omni: root.querySelector("[data-omni]"),
    omniInput: root.querySelector(".ifx-omni__input[name=\"q\"]"),
    omniPopover: root.querySelector("[data-omni-popover]"),
    omniClear: root.querySelector('[data-action="omni-clear"]'),
    intents: Array.from(root.querySelectorAll(".ifx-intent")),
    popular: root.querySelector("[data-popular]"),
    stats: root.querySelector("[data-stats]"),
    hero: root.querySelector(".ifx-hero"),
    bearing: root.querySelector("[data-bearing]"),
    model: root.querySelector("[data-model]"),
    stickybar: root.querySelector("[data-stickybar]"),
    stickyInput: root.querySelector("[data-stickybar-input]"),
    stickyMode: root.querySelector("[data-stickybar-mode]"),
    stickyForm: root.querySelector("[data-stickybar-form]"),
    dimsForm: root.querySelector('.ifx-panel[data-mode="dims"]'),
    decodeForm: root.querySelector('.ifx-panel[data-mode="decode"]'),
    pnForm: root.querySelector('.ifx-panel[data-mode="pn"]'),
    oemForm: root.querySelector('.ifx-panel[data-mode="oem"]'),
    densityToggle: root.querySelector("[data-density-toggle]"),
    brandHint: root.querySelector("[data-brand-hint]"),
    brandHintName: root.querySelector("[data-brand-hint-name]"),
    commonSizes: root.querySelector("[data-common-sizes]"),
    commonSizesChips: root.querySelector("[data-common-sizes-chips]"),
  };
  var pnMirror = refs.pnForm ? refs.pnForm.querySelector("[data-omni-mirror]") : null;
  var omniInput = refs.omniInput || pnMirror;

  /* ── app object shared with the views ────────────────────────────────── */
  var TAB_NAMES = { pn: "Interchange", decode: "Decode", dims: "Dimensions", oem: "Equipment", catalogs: "Catalogs" };
  var currentTab = "pn";
  var unit = IFX.state.prefs.get("unit", root.dataset.defaultUnit === "in" ? "in" : "mm");
  var density = IFX.state.prefs.get("density", root.dataset.defaultDensity === "table" ? "table" : "cards");
  var brandHint = null;

  var app = {
    root: root, api: IFX.api, refs: refs, esc: esc, track: IFX.track,
    get unit() { return unit; },
    get density() { return density; },
    get currentTab() { return currentTab; },
    get brandHint() { return brandHint; },
    status: setStatus, skeletons: renderSkeletons, error: renderError, clear: clearResults, head: showHead,
    activateTab: activateTabByName, openRfq: function (pn, brand, ctx) { IFX.rfq.open(pn, brand, ctx); },
    goSearch: goSearch, goDims: goDims, goDecode: goDecode, goFamily: goFamily, goEquipment: goEquipment,
    lazy: lazy, setUnit: setUnit, setBrandHint: setBrandHint,
    reveal: function (container) { IFX.motion.reveal(container); },
  };

  function setStatus(html, isError) {
    if (!refs.status) return;
    refs.status.innerHTML = html || "";
    refs.status.classList.toggle("is-error", !!isError);
    if (html) refs.head.hidden = false;
    if (!html && !IFX.views.search.hasResults()) refs.head.hidden = true;
  }
  function showHead(show) {
    refs.head.hidden = false;
    refs.head.classList.toggle("is-tools", !!show);
  }
  function clearResults(alsoStatus) {
    refs.results.innerHTML = "";
    root.querySelector("[data-filters]").hidden = true;
    refs.head.classList.remove("is-tools");
    if (alsoStatus !== false) { setStatus(""); refs.head.hidden = true; }
  }
  function renderSkeletons(kind) {
    refs.results.innerHTML = "";
    if (kind === "block") {
      refs.results.innerHTML = '<div class="ifx-skeleton-block" aria-hidden="true"></div>';
      return;
    }
    var grid = document.createElement("div");
    grid.className = "ifx-grid";
    for (var i = 0; i < 6; i++) {
      grid.insertAdjacentHTML("beforeend",
        '<div class="ifx-card ifx-card--skeleton" aria-hidden="true"><div class="ifx-skel ifx-skel--badge"></div><div class="ifx-skel ifx-skel--pn"></div><div class="ifx-skel ifx-skel--spec"></div><div class="ifx-skel ifx-skel--type"></div><div class="ifx-skel ifx-skel--cta"></div></div>');
    }
    refs.results.appendChild(grid);
  }
  function renderError(title, detail, retryFn) {
    refs.results.innerHTML = "";
    refs.head.classList.remove("is-tools");
    var box = document.createElement("div");
    box.className = "ifx-error";
    box.innerHTML = '<div class="ifx-error__title">' + esc(title) + "</div><p>" + esc(detail) + "</p>";
    if (typeof retryFn === "function") {
      var retry = document.createElement("button");
      retry.type = "button"; retry.className = "ifx-btn ifx-btn--ghost"; retry.textContent = "Try again";
      retry.addEventListener("click", retryFn);
      box.appendChild(retry);
    }
    refs.results.appendChild(box);
    setStatus(esc(title), true);
  }

  /* ── lazy views ──────────────────────────────────────────────────────── */
  var loading = {};
  function lazy(name) {
    if (IFX.views[name]) return Promise.resolve(IFX.views[name]);
    if (!loading[name]) {
      var src = root.dataset["asset" + name.charAt(0).toUpperCase() + name.slice(1)];
      loading[name] = new Promise(function (resolve, reject) {
        if (!src) return reject(new Error("no asset for " + name));
        var s = document.createElement("script");
        s.src = src; s.defer = true;
        s.onerror = function () { delete loading[name]; reject(new Error("failed to load " + name)); };
        document.head.appendChild(s);
        IFX.ready("view:" + name).then(function (view) { view.init(app); resolve(view); });
      });
    }
    return loading[name];
  }

  /* ── tabs ────────────────────────────────────────────────────────────── */
  function syncTabs() {
    refs.tabs.forEach(function (b) {
      var active = b.dataset.tab === currentTab;
      b.classList.toggle("is-active", active);
      b.setAttribute("aria-selected", active ? "true" : "false");
      b.tabIndex = active ? 0 : -1;
      if (active) IFX.motion.flip(refs.indicator, b);
    });
    refs.panels.forEach(function (p) { p.classList.toggle("is-active", p.dataset.mode === currentTab); });
    if (refs.stickyMode) refs.stickyMode.textContent = TAB_NAMES[currentTab] || "";
    root.dataset.tab = currentTab;
  }
  function activateTabByName(name, opts) {
    opts = opts || {};
    if (!refs.tabs.some(function (b) { return b.dataset.tab === name; })) name = "pn";
    var changed = currentTab !== name;
    currentTab = name;
    syncTabs();
    if (!opts.keepResults) clearResults();
    if (name === "oem") lazy("equipment").then(function (v) { v.open(opts); }).catch(lazyFail);
    if (name === "catalogs") lazy("catalogs").then(function (v) { v.open(); }).catch(lazyFail);
    if (changed && !opts.silent) IFX.track("ifx_tab_change", { tab: name });
  }
  function lazyFail(err) {
    console.error("[interchange] view failed to load", err);
    renderError("This section could not load", "Refresh the page to try again.", null);
  }
  refs.tabs.forEach(function (btn) {
    btn.addEventListener("click", function () { activateTabByName(btn.dataset.tab); });
    btn.addEventListener("keydown", function (e) {
      var idx = refs.tabs.indexOf(btn);
      if (e.key === "ArrowRight" || e.key === "ArrowDown") idx = (idx + 1) % refs.tabs.length;
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") idx = (idx - 1 + refs.tabs.length) % refs.tabs.length;
      else if (e.key === "Home") idx = 0;
      else if (e.key === "End") idx = refs.tabs.length - 1;
      else return;
      e.preventDefault();
      refs.tabs[idx].focus();
      activateTabByName(refs.tabs[idx].dataset.tab);
    });
  });
  window.addEventListener("resize", IFX.debounce(function () { syncTabs(); }, 120));

  /* ── navigation helpers ──────────────────────────────────────────────── */
  function goSearch(q, opts) {
    opts = opts || {};
    if (omniInput) omniInput.value = q;
    if (refs.stickyInput) refs.stickyInput.value = q;
    hidePopover();
    activateTabByName("pn", { silent: true });
    if (opts.brand !== undefined) setBrandHint(opts.brand);
    return IFX.views.search.runPn(q, { brand: opts.brand !== undefined ? opts.brand : brandHint });
  }
  function goFamily(fk) {
    activateTabByName("pn", { silent: true });
    if (omniInput) omniInput.value = fk;
    return IFX.views.search.runFamily(fk);
  }
  function goDims(p) {
    activateTabByName("dims", { silent: true });
    fillDims(p);
    updateSection();
    if (p.tol) setTol(p.tol);
    return IFX.views.search.runDims(Object.assign({ tol: currentTol() }, p));
  }
  function goDecode(q, brand) {
    activateTabByName("decode", { silent: true });
    var input = refs.decodeForm.querySelector('input[name="decode_q"]');
    var sel = refs.decodeForm.querySelector('select[name="decode_brand"]');
    if (input) input.value = q || "";
    if (sel) sel.value = brand || "";
    return lazy("decode").then(function (v) { return v.run(q, brand || (sel ? sel.value : "")); }).catch(lazyFail);
  }
  function goEquipment(opts) {
    activateTabByName("oem", Object.assign({ silent: true }, opts || {}));
  }
  function setBrandHint(slug) {
    brandHint = slug || null;
    if (!refs.brandHint) return;
    refs.brandHint.hidden = !brandHint;
    if (brandHint && refs.brandHintName) refs.brandHintName.textContent = IFX.brandName(brandHint);
    var pnHint = refs.pnForm ? refs.pnForm.querySelector("[data-pn-hint]") : null;
    if (pnHint) pnHint.hidden = !!brandHint;
  }
  var clearHintBtn = root.querySelector('[data-action="clear-brand-hint"]');
  if (clearHintBtn) clearHintBtn.addEventListener("click", function () {
    setBrandHint(null);
    var q = omniInput ? IFX.parse.splitBrandPrefix(omniInput.value).rest : "";
    if (q) goSearch(q, { brand: null });
  });

  /* ── omnibox ─────────────────────────────────────────────────────────── */
  var AC_DEBOUNCE_MS = 180;
  var popItems = [];       // flat list of focusable rows: { el, pick() }
  var popIdx = -1;
  var lastIntent = "unknown";

  function setIntent(parsed) {
    lastIntent = parsed.intent;
    refs.intents.forEach(function (chip) {
      var on = chip.dataset.intent === parsed.intent;
      var alt = parsed.alternatives && parsed.alternatives.indexOf(chip.dataset.intent) >= 0;
      chip.classList.toggle("is-on", on);
      chip.classList.toggle("is-alt", !on && !!alt);
    });
  }
  function routeQuery(text, opts) {
    opts = opts || {};
    var parsed = IFX.parse.parseQuery(text);
    if (opts.force) parsed.intent = opts.force;
    IFX.track("ifx_omni_intent", { intent: parsed.intent, query: text });
    if (parsed.intent === "dims" && parsed.dims) {
      if (parsed.dims.unit === "in") setUnit("in");
      return goDims({ bore: parsed.dims.bore, od: parsed.dims.od, width: parsed.dims.width, tol: currentTol() });
    }
    if (parsed.intent === "decode") {
      var code = parsed.decode && parsed.decode.code ? parsed.decode.code : text;
      return goDecode(code, "");
    }
    if (parsed.intent === "equipment" && refs.oemForm) {
      return goEquipment({ query: text });
    }
    if (parsed.intent === "unknown" && !/\d/.test(text)) {
      // A word with no digits: ask the server before guessing.
      return IFX.api.intent(text).then(function (srv) {
        if (srv && srv.intent === "equipment" && refs.oemForm) return goEquipment({ query: text });
        return goSearch(parsed.pn || text, { brand: parsed.brandHint });
      }).catch(function () { return goSearch(text, { brand: null }); });
    }
    var q = parsed.pn || text;
    return goSearch(q, { brand: parsed.brandHint || null });
  }

  if (omniInput) {
    var form = omniInput.form;
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var text = omniInput.value.trim();
      if (!text) { omniInput.focus(); return; }
      if (popIdx >= 0 && popItems[popIdx]) { popItems[popIdx].pick(); return; }
      routeQuery(text);
    });
    omniInput.addEventListener("input", function () {
      var v = omniInput.value.trim();
      if (refs.omniClear) refs.omniClear.hidden = v === "";
      if (v.length < 2) { IFX.api.abort("ac"); setIntent({ intent: "unknown" }); showIdlePopover(); return; }
      var parsed = IFX.parse.parseQuery(v);
      setIntent(parsed);
      if (parsed.intent === "pn" || parsed.intent === "unknown") debouncedAc(parsed.pn || v, parsed.brandHint);
      else if (parsed.intent === "equipment" && refs.oemForm) debouncedOem(v);
      else hidePopover();
    });
    omniInput.addEventListener("focus", function () { if (omniInput.value.trim().length < 2) showIdlePopover(); });
    omniInput.addEventListener("keydown", function (e) {
      if (e.key === "Escape") { hidePopover(); return; }
      if (popItems.length === 0) return;
      if (e.key === "ArrowDown") { e.preventDefault(); popIdx = (popIdx + 1) % popItems.length; highlightPop(); }
      else if (e.key === "ArrowUp") { e.preventDefault(); popIdx = (popIdx - 1 + popItems.length) % popItems.length; highlightPop(); }
      else if ((e.key === "ArrowRight" || e.key === "ArrowLeft") && popIdx >= 0 && popItems[popIdx].chips) {
        e.preventDefault();
        var row = popItems[popIdx];
        row.chipIdx = row.chipIdx == null ? 0 : row.chipIdx + (e.key === "ArrowRight" ? 1 : -1);
        if (row.chipIdx < 0) row.chipIdx = null; else if (row.chipIdx >= row.chips.length) row.chipIdx = row.chips.length - 1;
        highlightPop();
      }
    });
    if (refs.omniClear) refs.omniClear.addEventListener("click", function () { omniInput.value = ""; refs.omniClear.hidden = true; setIntent({ intent: "unknown" }); omniInput.focus(); showIdlePopover(); });
    refs.intents.forEach(function (chip) {
      chip.setAttribute("role", "button"); chip.tabIndex = 0;
      var go = function () { var t = omniInput.value.trim(); if (t) routeQuery(t, { force: chip.dataset.intent }); else omniInput.focus(); };
      chip.addEventListener("click", go);
      chip.addEventListener("keydown", function (e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); go(); } });
    });
  }

  var debouncedAc = IFX.debounce(function (q, brand) { fetchAutocomplete(q, brand); }, AC_DEBOUNCE_MS);
  var debouncedOem = IFX.debounce(function (q) { fetchOemSuggest(q); }, 220);

  function showPopover(html, items) {
    if (!refs.omniPopover) return;
    refs.omniPopover.innerHTML = html;
    refs.omniPopover.hidden = false;
    omniInput.setAttribute("aria-expanded", "true");
    popItems = items || [];
    popIdx = -1;
    highlightPop();
  }
  function hidePopover() {
    if (!refs.omniPopover) return;
    refs.omniPopover.hidden = true;
    refs.omniPopover.innerHTML = "";
    popItems = []; popIdx = -1;
    if (omniInput) { omniInput.setAttribute("aria-expanded", "false"); omniInput.removeAttribute("aria-activedescendant"); }
  }
  function highlightPop() {
    popItems.forEach(function (it, i) {
      var on = i === popIdx;
      it.el.classList.toggle("is-highlighted", on);
      it.el.setAttribute("aria-selected", on ? "true" : "false");
      if (it.chips) it.chips.forEach(function (c, j) { c.classList.toggle("is-highlighted", on && it.chipIdx === j); });
      if (!on) it.chipIdx = null;
    });
    if (popIdx >= 0 && popItems[popIdx]) omniInput.setAttribute("aria-activedescendant", popItems[popIdx].el.id);
    else omniInput.removeAttribute("aria-activedescendant");
  }
  function bindRows(container, items) {
    items.forEach(function (it, i) {
      it.el.id = "ifx-opt-" + i;
      it.el.addEventListener("mousedown", function (e) { e.preventDefault(); });
      it.el.addEventListener("click", function (e) {
        var chip = e.target.closest("[data-brand-chip]");
        if (chip && it.chips) { var j = it.chips.indexOf(chip); it.chipIdx = j; }
        it.pick();
      });
    });
  }

  function showIdlePopover() {
    if (!refs.omniPopover || document.activeElement !== omniInput) return;
    var recent = IFX.state.recent.list();
    var saved = IFX.state.saved.list().slice(0, 6);
    if (!recent.length && !saved.length) { hidePopover(); return; }
    var html = "", items = [];
    if (recent.length) {
      html += '<div class="ifx-pop__section"><span class="ifx-pop__label">Recent</span><button type="button" class="ifx-link ifx-pop__clear" data-clear-recent>Clear</button></div>';
      html += recent.map(function (r, i) { return '<div class="ifx-pop__row" role="option" data-recent="' + i + '"><span class="ifx-pop__main ifx-pn">' + esc(r.q) + '</span><span class="ifx-pop__meta">' + esc(r.intent === "dims" ? "size" : r.intent === "decode" ? "decode" : r.intent === "equipment" ? "machine" : "part number") + "</span></div>"; }).join("");
    }
    if (saved.length) {
      html += '<div class="ifx-pop__section"><span class="ifx-pop__label">Saved parts</span></div>';
      html += saved.map(function (s, i) { return '<div class="ifx-pop__row" role="option" data-saved="' + i + '"><span class="ifx-pop__main"><span class="ifx-brand">' + esc(IFX.brandName(s.brand)) + '</span> <span class="ifx-pn">' + esc(s.partNumber) + '</span></span><span class="ifx-pop__meta">' + esc(IFX.fmt.dims(s, unit)) + "</span></div>"; }).join("");
    }
    showPopover(html, []);
    var rows = [];
    refs.omniPopover.querySelectorAll("[data-recent]").forEach(function (el) {
      var r = recent[Number(el.dataset.recent)];
      rows.push({ el: el, pick: function () { omniInput.value = r.q; routeQuery(r.q, r.intent === "dims" ? { force: "dims" } : {}); } });
    });
    refs.omniPopover.querySelectorAll("[data-saved]").forEach(function (el) {
      var s = saved[Number(el.dataset.saved)];
      rows.push({ el: el, pick: function () { goSearch(s.partNumber, { brand: s.brand }); } });
    });
    var clear = refs.omniPopover.querySelector("[data-clear-recent]");
    if (clear) clear.addEventListener("click", function () { IFX.state.recent.clear(); showIdlePopover(); });
    popItems = rows;
    bindRows(refs.omniPopover, rows);
  }

  function fetchAutocomplete(q, brand) {
    IFX.api.autocomplete(q, 8).then(function (data) {
      if (!omniInput || omniInput.value.trim().length < 2) return hidePopover();
      var groups = data.groups || [];
      if (!groups.length) {
        var sugg = data.suggestions || [];
        if (!sugg.length) return showPopover('<div class="ifx-pop__empty">No suggestions. Press Enter to search anyway.</div>', []);
        var items = [];
        showPopover(sugg.map(function (s, i) { return '<div class="ifx-pop__row" role="option" data-s="' + i + '"><span class="ifx-pop__main ifx-pn">' + esc(s.partNumber) + '</span><span class="ifx-pop__meta">' + esc(IFX.brandName(s.brand)) + "</span></div>"; }).join(""), []);
        refs.omniPopover.querySelectorAll("[data-s]").forEach(function (el) { var s = sugg[Number(el.dataset.s)]; items.push({ el: el, pick: function () { IFX.track("ifx_autocomplete_pick", { pn: s.partNumber, brand: s.brand || "" }); goSearch(s.partNumber, { brand: s.brand || brand || null }); } }); });
        popItems = items; bindRows(refs.omniPopover, items);
        return;
      }
      var html = groups.map(function (g, i) {
        var dims = g.boreMm != null ? IFX.fmt.mm(g.boreMm, unit) + " × " + IFX.fmt.mm(g.odMm, unit) + (g.widthMm != null ? " × " + IFX.fmt.mm(g.widthMm, unit) : "") : "";
        return '<div class="ifx-pop__row ifx-pop__row--family" role="option" data-g="' + i + '">'
          + '<div class="ifx-pop__line"><span class="ifx-pop__main ifx-pn">' + esc(g.familyKey) + "</span>"
          + (g.bearingType ? '<span class="ifx-pop__type">' + esc(IFX.typeName(g.bearingType)) + "</span>" : "")
          + '<span class="ifx-pop__meta">' + esc(dims) + (g.brandCount ? (dims ? " · " : "") + g.brandCount + " brand" + (g.brandCount === 1 ? "" : "s") : "") + "</span></div>"
          + (g.brands && g.brands.length ? '<div class="ifx-pop__chips">' + g.brands.map(function (b) { return '<span class="ifx-pop__chip" data-brand-chip="' + esc(b) + '">' + esc(IFX.brandName(b)) + "</span>"; }).join("") + "</div>" : "")
          + "</div>";
      }).join("");
      showPopover(html, []);
      var rows = [];
      refs.omniPopover.querySelectorAll("[data-g]").forEach(function (el) {
        var g = groups[Number(el.dataset.g)];
        var chips = Array.from(el.querySelectorAll("[data-brand-chip]"));
        var row = { el: el, chips: chips, chipIdx: null, pick: function () {
          var b = row.chipIdx != null && chips[row.chipIdx] ? chips[row.chipIdx].dataset.brandChip : null;
          IFX.track("ifx_autocomplete_pick", { pn: g.familyKey, brand: b || "" });
          goSearch(g.familyKey, { brand: b || brand || null });
        } };
        rows.push(row);
      });
      popItems = rows; bindRows(refs.omniPopover, rows);
    }).catch(function (err) { if (!IFX.api.isAbort(err)) hidePopover(); });
  }

  function fetchOemSuggest(q) {
    IFX.api.equipment({ q: q }, "ac").then(function (data) {
      var machines = data.machines || [];
      if (!machines.length) return showPopover('<div class="ifx-pop__empty">No machines matched. Press Enter to search it as a part number, or browse equipment by category.</div>', []);
      showPopover('<div class="ifx-pop__section"><span class="ifx-pop__label">Machines</span></div>' + machines.slice(0, 8).map(function (m, i) {
        var n = Number(m.applicationCount) || 0;
        return '<div class="ifx-pop__row" role="option" data-m="' + i + '"><span class="ifx-pop__main">' + esc((m.manufacturer || "") + " " + (m.model || "")) + (m.machineType ? ' <span class="ifx-pop__type">' + esc(String(m.machineType).replace(/_/g, " ")) + "</span>" : "") + '</span><span class="ifx-pop__meta">' + n + " bearing" + (n === 1 ? "" : "s") + "</span></div>";
      }).join(""), []);
      var rows = [];
      refs.omniPopover.querySelectorAll("[data-m]").forEach(function (el) {
        var m = machines[Number(el.dataset.m)];
        rows.push({ el: el, pick: function () { hidePopover(); goEquipment({ machineId: m.id, label: (m.manufacturer || "") + " " + (m.model || "") }); } });
      });
      popItems = rows; bindRows(refs.omniPopover, rows);
    }).catch(function (err) { if (!IFX.api.isAbort(err)) hidePopover(); });
  }

  document.addEventListener("click", function (e) {
    if (refs.omniPopover && !refs.omniPopover.hidden && !e.target.closest(".ifx-omni__wrap")) hidePopover();
  });

  /* ── pn panel (no-hero variant) ──────────────────────────────────────── */
  if (refs.pnForm) refs.pnForm.addEventListener("submit", function (e) {
    e.preventDefault();
    var text = omniInput ? omniInput.value.trim() : "";
    if (text) routeQuery(text);
  });

  /* ── decode panel ────────────────────────────────────────────────────── */
  if (refs.decodeForm) {
    refs.decodeForm.addEventListener("submit", function (e) {
      e.preventDefault();
      var q = refs.decodeForm.querySelector('input[name="decode_q"]').value.trim();
      var b = refs.decodeForm.querySelector('select[name="decode_brand"]').value;
      if (!q) return setStatus("Enter a part number to decode.", true);
      goDecode(q, b);
    });
    root.querySelectorAll('[data-action="open-glossary"]').forEach(function (btn) {
      btn.addEventListener("click", function (e) { e.preventDefault(); activateTabByName("decode", { silent: true }); lazy("decode").then(function (v) { v.glossary(); }).catch(lazyFail); });
    });
  }

  /* ── dims panel ──────────────────────────────────────────────────────── */
  var unitButtons = Array.from(root.querySelectorAll("[data-unit-toggle] [data-unit]"));
  var tolButtons = Array.from(root.querySelectorAll("[data-tol-toggle] [data-tol]"));
  function setUnit(u) {
    var next = u === "in" ? "in" : "mm";
    var changed = next !== unit;
    unit = next;
    IFX.state.prefs.set("unit", unit);
    unitButtons.forEach(function (b) { var on = b.dataset.unit === unit; b.classList.toggle("is-active", on); b.setAttribute("aria-checked", on ? "true" : "false"); });
    root.querySelectorAll(".ifx-field--dim input").forEach(function (inp) {
      var ph = inp.dataset[unit === "mm" ? "unitPlaceholderMm" : "unitPlaceholderIn"];
      if (ph) inp.placeholder = ph;
    });
    if (changed) {
      convertDimInputs(next);
      updateSection();
      if (IFX.views.search.hasResults()) IFX.views.search.rerender();
      morphSpecs();
    }
  }
  function convertDimInputs(toUnit) {
    root.querySelectorAll(".ifx-field--dim input").forEach(function (inp) {
      var v = inp.value.trim();
      if (!v) return;
      var mm = IFX.parse.parseDimension(v, toUnit === "in" ? "mm" : "in");
      if (mm == null) return;
      inp.value = toUnit === "in" ? String(IFX.parse.mmToInches(mm)) : String(Math.round(mm * 1000) / 1000);
    });
  }
  function morphSpecs() {
    root.querySelectorAll(".ifx-specs dd[data-mm]").forEach(function (dd) {
      var mm = Number(dd.dataset.mm);
      if (!Number.isFinite(mm)) return;
      dd.textContent = IFX.fmt.mm(mm, unit);
      dd.classList.remove("is-morph"); void dd.offsetWidth; dd.classList.add("is-morph");
    });
  }
  unitButtons.forEach(function (btn) {
    btn.addEventListener("click", function () { setUnit(btn.dataset.unit); });
    btn.addEventListener("keydown", function (e) { radioArrows(e, unitButtons, btn, function (b) { setUnit(b.dataset.unit); }); });
  });
  function currentTol() { var on = tolButtons.find(function (b) { return b.classList.contains("is-active"); }); return on ? Number(on.dataset.tol) || 0.1 : 0.1; }
  function setTol(t) {
    var val = String(t);
    if (!tolButtons.some(function (b) { return b.dataset.tol === val; })) val = Number(t) >= 1 ? "1" : Number(t) >= 0.5 ? "0.5" : "0.1";
    tolButtons.forEach(function (b) { var on = b.dataset.tol === val; b.classList.toggle("is-active", on); b.setAttribute("aria-checked", on ? "true" : "false"); });
  }
  tolButtons.forEach(function (btn) {
    btn.addEventListener("click", function () { setTol(btn.dataset.tol); });
    btn.addEventListener("keydown", function (e) { radioArrows(e, tolButtons, btn, function (b) { setTol(b.dataset.tol); }); });
  });
  function radioArrows(e, list, btn, apply) {
    var idx = list.indexOf(btn);
    if (e.key === "ArrowRight" || e.key === "ArrowDown") idx = (idx + 1) % list.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") idx = (idx - 1 + list.length) % list.length;
    else return;
    e.preventDefault(); list[idx].focus(); apply(list[idx]);
  }
  function readDims() {
    var fd = new FormData(refs.dimsForm);
    var p = {};
    ["bore", "od", "width"].forEach(function (k) { var v = IFX.parse.parseDimension(fd.get(k), unit); if (v != null) p[k] = v; });
    p.tol = currentTol();
    return p;
  }
  function fillDims(p) {
    ["bore", "od", "width"].forEach(function (k) {
      var inp = refs.dimsForm.querySelector('input[name="' + k + '"]');
      if (!inp) return;
      var mm = p[k];
      if (mm == null || !Number.isFinite(Number(mm))) { inp.value = ""; return; }
      inp.value = unit === "in" ? String(IFX.parse.mmToInches(Number(mm))) : String(mm);
    });
  }
  if (refs.dimsForm) {
    refs.dimsForm.addEventListener("submit", function (e) {
      e.preventDefault();
      var p = readDims();
      if (p.bore == null && p.od == null && p.width == null) return setStatus("Enter at least one dimension, in " + (unit === "in" ? "inches" : "millimetres") + ".", true);
      IFX.views.search.runDims(p);
    });
    refs.dimsForm.querySelectorAll(".ifx-field--dim input").forEach(function (inp) { inp.addEventListener("input", updateSection); });
  }

  /* live cross-section: scales the rings to bore / OD / width (defaults 25/52/15) */
  var section = root.querySelector("[data-section-viz]");
  function updateSection() {
    if (!section) return;
    var p = readDims();
    var d = p.bore != null ? p.bore : null, D = p.od != null ? p.od : null, B = p.width != null ? p.width : null;
    var dd = d != null ? d : D != null ? D * 0.48 : 25;
    var DD = D != null ? D : d != null ? d * 2.08 : 52;
    var BB = B != null ? B : Math.max(6, Math.round(DD * 0.29));
    if (dd >= DD) dd = DD * 0.48;
    var scale = 150 / DD;               // OD maps to 150 px tall
    var cx = 160, cy = 110;
    var halfD = (DD * scale) / 2, halfd = (dd * scale) / 2, w = Math.max(24, Math.min(140, BB * scale));
    var ring = Math.max(10, (halfD - halfd) / 2.3);
    var x = cx - w / 2;
    var set = function (sel, attrs) { var el = section.querySelector(sel); if (!el) return; Object.keys(attrs).forEach(function (k) { el.setAttribute(k, String(Math.round(attrs[k] * 10) / 10)); }); };
    set("[data-sec-outer-top]", { x: x, y: cy - halfD, width: w, height: ring });
    set("[data-sec-outer-bot]", { x: x, y: cy + halfD - ring, width: w, height: ring });
    set("[data-sec-inner-top]", { x: x, y: cy - halfd - ring * 0.85, width: w, height: ring * 0.85 });
    set("[data-sec-inner-bot]", { x: x, y: cy + halfd, width: w, height: ring * 0.85 });
    var r = Math.max(4, Math.min(w / 2 - 2, (halfD - ring - halfd - ring * 0.85) / 2 - 1));
    set("[data-sec-ball-top]", { cx: cx, cy: cy - halfd - ring * 0.85 - r - 1, r: r });
    set("[data-sec-ball-bot]", { cx: cx, cy: cy + halfd + ring * 0.85 + r + 1, r: r });
    set("[data-sec-dim-bore]", { x1: x - 18, y1: cy - halfd, x2: x - 18, y2: cy + halfd });
    set("[data-sec-label-bore]", { x: x - 24, y: cy + 4 });
    set("[data-sec-dim-od]", { x1: x + w + 18, y1: cy - halfD, x2: x + w + 18, y2: cy + halfD });
    set("[data-sec-label-od]", { x: x + w + 26, y: cy + 4 });
    set("[data-sec-dim-width]", { x1: x, y1: cy + halfD + 14, x2: x + w, y2: cy + halfD + 14 });
    set("[data-sec-label-width]", { x: cx, y: cy + halfD + 28 });
    var lbl = function (sel, letter, v, given) { var el = section.querySelector(sel); if (el) el.textContent = letter + (v != null ? " " + IFX.fmt.mm(v, unit) : ""); if (el) el.classList.toggle("is-given", !!given); };
    lbl("[data-sec-label-bore]", "d", d, d != null); lbl("[data-sec-label-od]", "D", D, D != null); lbl("[data-sec-label-width]", "B", B, B != null);
    var cap = root.querySelector("[data-section-caption]");
    if (cap) cap.textContent = (d == null && D == null && B == null) ? "Cross-section scales with your entries." : "Section drawn to your entries, not to a catalog part.";
  }
  updateSection();

  /* ── density ─────────────────────────────────────────────────────────── */
  var densityButtons = Array.from(root.querySelectorAll("[data-density-toggle] [data-density]"));
  function setDensity(d) {
    density = d === "table" ? "table" : "cards";
    IFX.state.prefs.set("density", density);
    densityButtons.forEach(function (b) { var on = b.dataset.density === density; b.classList.toggle("is-active", on); b.setAttribute("aria-checked", on ? "true" : "false"); });
    root.dataset.density = density;
    if (IFX.views.search.hasResults()) { IFX.views.search.rerender(); IFX.state.write({ view: density === "table" ? "table" : null }, { merge: true, replace: true }); }
    IFX.track("ifx_density_change", { density: density });
  }
  densityButtons.forEach(function (b) { b.addEventListener("click", function () { setDensity(b.dataset.density); }); });
  densityButtons.forEach(function (b) { var on = b.dataset.density === density; b.classList.toggle("is-active", on); b.setAttribute("aria-checked", on ? "true" : "false"); });
  root.dataset.density = density;

  /* ── stats + popular ─────────────────────────────────────────────────── */
  function hydrateStats() {
    IFX.api.stats().then(function (s) {
      if (!s || !s.rounded) return;
      root.querySelectorAll("[data-stat]").forEach(function (dd) {
        var key = dd.dataset.stat;
        var v = s.rounded[key];
        if (v) IFX.motion.countUp(dd, v, { from: 0 });
      });
      root.querySelectorAll("[data-stat-text]").forEach(function (el) { var v = s.rounded[el.dataset.statText]; if (v) el.textContent = v; });
    }).catch(function () {
      root.querySelectorAll("[data-stat]").forEach(function (dd) { if (dd.dataset.fallback) IFX.motion.countUp(dd, dd.dataset.fallback, { from: 0 }); });
    });
  }
  function hydratePopular() {
    IFX.api.popular(12).then(function (p) {
      var fams = (p && p.families) || [];
      if (refs.popular && fams.length >= 6) {
        var chips = refs.popular.querySelector(".ifx-popular__chips");
        chips.innerHTML = fams.slice(0, 10).map(function (f) {
          var dims = f.boreMm != null ? " " + IFX.fmt.mm(f.boreMm, unit).replace(" mm", "") + "×" + IFX.fmt.mm(f.odMm, unit).replace(" mm", "") : "";
          return '<a class="ifx-chip" href="?q=' + encodeURIComponent(f.familyKey) + '" data-popular-q="' + esc(f.familyKey) + '" title="' + esc(IFX.typeName(f.bearingType) + (f.brandCount ? ", " + f.brandCount + " brands" : "")) + '">' + esc(f.familyKey) + '<span class="ifx-chip__dim">' + esc(dims) + "</span></a>";
        }).join("");
        wirePopular();
      }
      var sizes = (p && p.sizes) || [];
      if (refs.commonSizes && sizes.length) {
        refs.commonSizes.hidden = false;
        refs.commonSizesChips.innerHTML = sizes.slice(0, 8).map(function (z) {
          return '<button type="button" class="ifx-chip" data-size="' + z.boreMm + "|" + z.odMm + "|" + z.widthMm + '">' + esc(z.boreMm + " × " + z.odMm + " × " + z.widthMm) + "</button>";
        }).join("");
        refs.commonSizesChips.querySelectorAll("[data-size]").forEach(function (b) {
          b.addEventListener("click", function () { var s = b.dataset.size.split("|").map(Number); goDims({ bore: s[0], od: s[1], width: s[2], tol: currentTol() }); });
        });
      }
    }).catch(function () { /* keep server-rendered chips */ });
  }
  function wirePopular() {
    root.querySelectorAll("[data-popular-q]").forEach(function (a) {
      a.addEventListener("click", function (e) { e.preventDefault(); routeQuery(a.dataset.popularQ); });
    });
  }
  wirePopular();

  /* ── sticky bar ──────────────────────────────────────────────────────── */
  if (refs.stickybar && refs.hero) {
    var headerEl = document.querySelector("header.header, .header-wrapper, .site-header, .shopify-section-header, header");
    var headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
    refs.stickybar.style.setProperty("--ifx-header-h", Math.round(headerH) + "px");
    IFX.motion.sticky(refs.hero, function (on) { refs.stickybar.hidden = !on; refs.stickybar.classList.toggle("is-on", on); }, headerH);
    refs.stickyForm.addEventListener("submit", function (e) {
      e.preventDefault();
      var t = refs.stickyInput.value.trim();
      if (!t) return;
      routeQuery(t);
      root.scrollIntoView({ behavior: IFX.motionOK() ? "smooth" : "auto", block: "start" });
    });
  }

  /* ── shortcuts ───────────────────────────────────────────────────────── */
  var shortcutsDialog = root.querySelector('.ifx-dialog[data-modal="shortcuts"]');
  if (shortcutsDialog) {
    shortcutsDialog.querySelectorAll("[data-close]").forEach(function (b) { b.addEventListener("click", function () { shortcutsDialog.close(); }); });
    shortcutsDialog.addEventListener("click", function (e) { if (e.target === shortcutsDialog) shortcutsDialog.close(); });
  }
  document.addEventListener("keydown", function (e) {
    var t = e.target;
    var typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
    if (e.key === "/" && !typing && omniInput) { e.preventDefault(); omniInput.focus(); omniInput.select(); }
    else if (e.key === "?" && !typing && shortcutsDialog && typeof shortcutsDialog.showModal === "function") { e.preventDefault(); if (!shortcutsDialog.open) shortcutsDialog.showModal(); }
  });

  /* ── disclaimer Esc ──────────────────────────────────────────────────── */
  var disclaimer = root.querySelector(".ifx-disclaimer");
  if (disclaimer) disclaimer.addEventListener("keydown", function (e) { if (e.key === "Escape") { e.stopPropagation(); disclaimer.blur(); } });

  /* ── URL restore ─────────────────────────────────────────────────────── */
  function applyState(st) {
    var tab = st.tab || (st.q ? "pn" : st.family ? "pn" : null);
    if (st.view === "table" && density !== "table") setDensity("table");
    if (!tab) { activateTabByName("pn", { silent: true }); return; }
    if (tab === "pn") {
      activateTabByName("pn", { silent: true });
      if (st.family) { if (omniInput) omniInput.value = st.family; IFX.views.search.runFamily(st.family); return; }
      var q = (st.q || "").trim();
      if (q) { if (omniInput) omniInput.value = q; if (refs.omniClear) refs.omniClear.hidden = false; setIntent(IFX.parse.parseQuery(q)); setBrandHint(st.brand || IFX.parse.parseQuery(q).brandHint || null); IFX.views.search.runPn(q, { brand: brandHint }); }
    } else if (tab === "dims") {
      activateTabByName("dims", { silent: true });
      if (st.unit === "in") setUnit("in");
      if (st.tol) setTol(st.tol);
      var p = {};
      ["bore", "od", "width"].forEach(function (k) { if (st[k] != null && st[k] !== "" && Number.isFinite(Number(st[k]))) p[k] = Number(st[k]); });
      fillDims(p); updateSection();
      if (p.bore != null || p.od != null || p.width != null) IFX.views.search.runDims(Object.assign({ tol: currentTol() }, p));
    } else if (tab === "decode") {
      var dq = (st.q || "").trim();
      if (dq) goDecode(dq, st.brand || ""); else activateTabByName("decode", { silent: true });
    } else if (tab === "oem") {
      activateTabByName("oem", { silent: true, machineId: st.machineId || null, query: st.q || null, cat: st.cat || null, mfr: st.mfr || null, model: st.model || null });
    } else if (tab === "catalogs") {
      activateTabByName("catalogs", { silent: true });
    } else activateTabByName("pn", { silent: true });
  }
  IFX.state.onPop(applyState);

  /* ── init ────────────────────────────────────────────────────────────── */
  IFX.views.search.init(app);
  IFX.rfq.init(app);
  IFX.motion.bearing(refs.bearing);
  IFX.motion.model(refs.model);
  setUnit(unit);
  syncTabs();
  hydrateStats();
  hydratePopular();
  IFX.api.warm();
  if (IFX.state.hasOwnedParams()) {
    IFX.state.markPushed();
    IFX.state.restore(applyState);
  } else {
    syncTabs();
  }
  requestAnimationFrame(function () { root.classList.add("is-ready"); syncTabs(); });
})();
